import { classNames } from 'primereact/utils';
import { useEffect, useRef, useState } from 'react';

import { IconShowMore } from '@/components/icons';

type Props = {
  isRead: boolean;
  setIsRead: (isRead: boolean) => void;
};

const RegisterAccountTermService = ({ isRead, setIsRead }: Props) => {
  const [isShowMore, setIsShowMore] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);

  const handleShowMore = () => {
    setIsShowMore((prev) => !prev);
  };

  const handleScroll = () => {
    if (!contentRef.current || isRead) return;

    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;

    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setIsRead(true);
    }
  };

  useEffect(() => {
    const contentEl = contentRef.current;
    if (contentEl) {
      contentEl.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (contentEl) {
        contentEl.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isRead]);

  return (
    <div className="text-gray-800 text-sm  mt-[6px]">
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div
          className={classNames('transition-all', {
            'max-h-[250px] overflow-hidden overflow-y-scroll': !isShowMore,
            'max-h-[500px] overflow-hidden overflow-y-scroll': isShowMore,
          })}
          ref={contentRef}
        >
          <p>アート・シフト・トレーニングアプリ 利用規約</p>
          <p>
            この利用規約は、お客様が、一般社団法人アート・シフト・コーチング協会（以下「当協会」）が提供する「アート・シフト・トレーニングアプリ」（以下「本アプリ」）をご利用頂く際の取扱いにつき定めるものです。本利用規約に同意した上で本アプリをご利用ください。
          </p>

          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">第１条（定義）</h3>
            <p className="ml-2 mt-2">
              本利用規約上で使用する用語の定義は、次に掲げるとおりとします。
            </p>

            <table className="w-full mt-3 border-collapse">
              <tbody>
                {[
                  [
                    '（1）本アプリ',
                    '当協会が運営する「アート・シフト・トレーニングアプリ」および関連するサービス',
                  ],
                  [
                    '（2）利用者',
                    '本アプリを利用する全ての方で、アートシフトコーチおよび受講生を含む',
                  ],
                  [
                    '（3）アートシフトコーチ',
                    '当協会が認定したアートシフトコーチで、当協会に対してアカウント申請し、本アプリのＩＤ登録が完了した者',
                  ],
                  [
                    '（4）受講生',
                    'アートシフトコーチが自ら追加・登録する利用者',
                  ],
                  [
                    '（5）ＩＤ',
                    '本アプリの利用のためにアートシフトコーチが固有に持つ文字列',
                  ],
                  [
                    '（6）パスワード',
                    'ＩＤに対応してアートシフトコーチに固有に設定する暗号',
                  ],
                  [
                    '（7）個人情報',
                    '住所、氏名、性別、生年月日（年齢）、職業、電話番号等個人を特定することのできる情報の総称',
                  ],
                  [
                    '（8）登録情報',
                    'アートシフトコーチが本アプリのＩＤ登録にて登録した情報の総称（投稿情報は除く）',
                  ],
                  [
                    '（9）知的財産',
                    '発明、考案、植物の新品種、意匠、著作物その他の人間の創造的活動により生み出されるもの...',
                  ],
                  [
                    '（10）知的財産権',
                    '特許権、実用新案権、育成者権、意匠権、著作権、商標権その他の知的財産に関して法令により定められた権利または法律上保護される利益に係る権利',
                  ],
                ].map(([term, desc], i) => (
                  <tr key={i}>
                    <td className="border border-gray-200 px-3 py-2 align-top w-40 text-nowrap">
                      {term}
                    </td>
                    <td className="border border-gray-200 px-3 py-2">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">
              第２条（本利用規約への同意）
            </h3>
            <ol className="list-decimal pl-6 space-y-2">
              <li>
                利用者は、本利用規約に同意頂いた上で、本アプリを利用できるものとします。
              </li>
              <li>
                利用者が、本アプリをスマートフォンその他の情報端末にダウンロードし、本利用規約への同意手続を行った時点で、利用者と当協会との間で、本利用規約の諸規定に従った利用契約が成立するものとします。
              </li>
              <li>
                利用者が未成年者である場合には、親権者その他の法定代理人の同意を得たうえで、本アプリをご利用ください。
              </li>
              <li>
                未成年者の利用者が、法定代理人の同意がないにもかかわらず同意があると偽りまたは年齢について成年と偽って本アプリを利用した場合、その他行為能力者であることを信じさせるために詐術を用いた場合、本アプリに関する一切の法律行為を取り消すことは出来ません。
              </li>
              <li>
                本利用規約の同意時に未成年であった利用者が成年に達した後に本アプリを利用した場合、当該利用者は本アプリに関する一切の法律行為を追認したものとみなされます。
              </li>
            </ol>
          </section>

          <section className="mt-6">
            <h3 className="text-lg font-bold mt-6 mb-2">
              第３条（規約の改定）
            </h3>
            <ol className="list-decimal pl-6 space-y-2">
              <li>
                当協会は、アートシフトコーチの承諾を得ることなく、いつでも、本利用規約の内容を改定することができるものとし、アートシフトコーチはこれを異議なく同意するものとします。
              </li>
              <li>
                当協会は、本利用規約を改定するときは、その内容について当協会所定の方法により利用者に通知します。
              </li>
              <li>
                本利用規約の改定の効力は、当協会が前項により通知を行った時点から生じるものとします。
              </li>
              <li>
                利用者は、本利用規約改定後、本アプリを利用した時点で、改定後の本利用規約に異議なく同意したものとみなされます。
              </li>
            </ol>
          </section>

          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">
              第４条（登録希望者のアカウント申請）
            </h3>
            <ol className="list-decimal pl-6 space-y-2">
              <li>
                本アプリの利用を希望する方（以下「登録希望者」）は、本利用規約に同意した上で、所定の方法でアカウント申請を行ってください。
              </li>
              <li>
                アカウント申請した方は、当協会がその申込を承諾し、ＩＤ登録が完了した時点からアートシフトコーチとなります。
              </li>
              <li>
                当協会は、アートシフトコーチ向けに電子メールで連絡事項の告知や広告その他の情報提供を行います。あらかじめご承諾ください。
              </li>
              <li>
                当協会は、登録希望者が次の各号のいずれか一つに該当する場合は、当協会の判断によりＩＤ登録を承諾しないことがあります。
                <ol className="list-decimal pl-6 space-y-1 mt-2">
                  <li>
                    登録希望者が、当協会の定める方法によらずアカウント申請を行った場合
                  </li>
                  <li>
                    登録希望者が、過去に本利用規約または当協会の定めるその他の利用規約等に違反したことを理由として退会処分を受けた者である場合
                  </li>
                  <li>
                    登録希望者が、不正な手段をもってアカウント申請またはＩＤ登録を行っていると当協会が判断した場合
                  </li>
                  <li>登録希望者が、本人以外の情報を登録している場合</li>
                  <li>その他当協会が不適切と判断した場合</li>
                </ol>
              </li>
            </ol>
          </section>

          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">
              第５条（受講生の管理・提供コンテンツ）
            </h3>
            <ol className="list-decimal pl-6 space-y-2">
              <li>
                アートシフトコーチは、本利用規約に同意した上で、１０人を上限として自身の受講生を本アプリに追加することができます。なお、アートシフトコーチは自らの責任で受講生を管理するものとします。
              </li>
              <li>
                アートシフトコーチの申込プランによっては、前項の上限値はこの限りではありません。
              </li>
              <li>
                アートシフトコーチは、自らが、自らの受講生に提供するコンテンツ（動画やクイズ等）に対する一切の責を負うものとし、これらのコンテンツについて当協会は一切関知せず、なんらの責任も負うことはできません。ただし、当協会が、アートシフトコーチがアップロードしたコンテンツに問題があると合理的に判断した場合、当協会は、当該コンテンツを削除することができるものとし、当該アートシフトコーチはこれに異議を申立てることはできないものとします。
              </li>
              <li>
                アートシフトコーチが、第７条に規定する利用料金の支払いを怠った場合、当該アートシフトコーチの受講生も、本アプリへのアクセス権を直ちに失うものとし、以後、利用料金の支払いが確認されるまでの間、当該アートシフトコーチの受講生も本アプリを利用することはできません。
              </li>
            </ol>
          </section>

          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">第７条（利用料金）</h3>
            <ol className="list-decimal pl-6 space-y-2">
              <li>
                アートシフトコーチは、当協会が規定する料金体系に応じた利用料金を、当協会が指定する方法により支払うものとします。
              </li>
              <li>
                前項に掲げる利用料金は、いかなる理由によっても一切払い戻しはできません。
              </li>
              <li>
                当協会は、利用料金の決済および情報処理に、第三者決済処理サービスを利用する場合があります。当協会は、当協会の故意または重過失に起因する場合を除き、第三者決済処理サービスの利用に起因もしくは関連する論争、クレーム、訴訟、権利侵害、または損害について一切の責任を負いません。
              </li>
            </ol>
          </section>

          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">
              第８条（アカウントの管理）
            </h3>
            <ol className="list-decimal pl-6 space-y-2">
              <li>
                アートシフトコーチは、利用に際して登録した情報（電子メールアドレス、ＩＤやパスワード等を含みます）について、自己の責任の下、任意に登録、管理するものとします。アートシフトコーチは、これを第三者に利用させ、または貸与、譲渡、名義変更、売買などをしてはならないものとします。
              </li>
              <li>
                当協会は、登録情報によって本アプリの利用があった場合、アカウント申請をおこなった本人が利用したものと扱うことができ、当該利用によって生じた結果ならびにそれに伴う一切の責任については、アカウント申請を行った本人に帰属するものとします。
              </li>
              <li>
                アートシフトコーチは、登録情報の不正使用によって当協会または第三者に損害が生じた場合、当協会および第三者に対して、当該損害を賠償するものとします。
              </li>
              <li>
                登録情報の管理は、アートシフトコーチが自己の責任の下で行うものとし、登録情報が不正確または虚偽であったために当該アートシフトコーチが被った一切の不利益および損害に関して、当協会は責任を負わないものとします。
              </li>
              <li>
                登録情報が盗用されまたは第三者に利用されていることが判明した場合、アートシフトコーチは直ちにその旨を当協会に通知するとともに、当協会からの指示に従うものとします。
              </li>
              <li>
                アートシフトコーチが追加・登録した受講生について、第１項ないし前項の規定を準用します。
              </li>
            </ol>
          </section>

          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">
              第９条（個人情報等の取り扱い）
            </h3>
            <p className="pl-6">
              当協会は、個人情報および登録情報について、当協会が別途定めるプライバシーポリシーに則り、適正に取り扱うこととします。
            </p>
          </section>

          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">第１０条（禁止行為）</h3>
            <ol className="list-decimal pl-6 space-y-4">
              <li>
                本アプリの利用に際し、当協会は、利用者に対し、次に掲げる行為を禁止します。当協会において、利用者が禁止事項に違反したと認めた場合、当該利用者の一時利用停止、退会処分その他当協会が必要と判断した措置を取ることができます。
                <ol className="list-decimal pl-6 mt-2 space-y-1">
                  <li>当協会または第三者の知的財産権を侵害する行為</li>
                  <li>
                    当協会または第三者の名誉・信用を毀損または不当に差別もしくは誹謗中傷する行為
                  </li>
                  <li>
                    当協会または第三者の財産を侵害する行為、または侵害する恐れのある行為
                  </li>
                  <li>当協会または第三者に経済的損害を与える行為</li>
                  <li>当協会または第三者に対する脅迫的な行為</li>
                  <li>
                    コンピューターウィルス、有害なプログラムを使用またはそれを誘発する行為
                  </li>
                  <li>
                    本アプリ用インフラ設備に対して過度な負担となるストレスをかける行為
                  </li>
                  <li>
                    当協会Ｗｅｂサイトのサーバーやシステム、セキュリティへの攻撃
                  </li>
                  <li>
                    当協会提供のインターフェース以外の方法で当協会サービスにアクセスを試みる行為
                  </li>
                  <li>一人のアートシフトコーチが、複数のＩＤを取得する行為</li>
                  <li>上記の他、当協会が不適切と判断する行為</li>
                </ol>
              </li>
              <li>
                本アプリにおいて、アートシフトコーチは、以下に定める内容・表現を含むもしくはその恐れのあるコンテンツのアップロードを行ってはならないものとします。アートシフトコーチが、この禁止事項に違反したと当協会が判断した場合には、当協会は、第５条第３項の規定に従い削除処分、その他の措置をとることができるものとします。
                <ol className="list-decimal pl-6 mt-2 space-y-1">
                  <li>良識に欠けるものや、品位に欠けるもの</li>
                  <li>
                    他の利用者、その他第三者および当協会を中傷したり、名誉を傷つけたりするもの、権利を侵害するもの
                  </li>
                  <li>社会倫理や法令に反するもの</li>
                  <li>当協会の承諾のない商業行為を含むもの</li>
                  <li>
                    その他、個人、法人問わず、自らまたは組織等を偽る内容、宗教団体もしくはそれと同視し得る団体への勧誘、布教、寄付等を求める、またはその恐れのあるもの
                  </li>
                </ol>
              </li>
            </ol>
          </section>

          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">第１１条（免責）</h3>
            <ol className="list-decimal pl-6 space-y-2">
              <li>
                当協会は、本アプリの内容変更、中断、終了によって生じたいかなる損害についても、一切責任を負いません。
              </li>
              <li>
                当協会は、利用者の本アプリの利用環境について一切関与せず、また一切の責任を負いません。
              </li>
              <li>
                当協会は、本アプリが利用者の特定の目的に適合すること、期待する機能・商品的価値・正確性・有用性を有すること、利用者による本アプリの利用が利用者に適用のある法令または業界団体の内部規則等に適合すること、および不具合が生じないことについて、何ら保証するものではありません。
              </li>
              <li>
                当協会は、本アプリが全ての情報端末に対応していることを保証するものではなく、本アプリの利用に供する情報端末のＯＳのバージョンアップ等に伴い、本アプリの動作に不具合が生じる可能性があることにつき、利用者はあらかじめ承諾するものとします。当協会は、かかる不具合が生じた場合に当協会が行うプログラムの修正等により、当該不具合が解消されることを保証するものではありません。
              </li>
              <li>
                利用者は、AppStore、GooglePlay等のサービスストアの利用規約および運用方針の変更等に伴い、本アプリの一部または全部の利用が制限される可能性があることをあらかじめ承諾するものとします。
              </li>
              <li>
                当協会は、本アプリを利用したことにより直接的または間接的に利用者に発生した損害について、一切賠償責任を負いません。
              </li>
              <li>
                当協会は、利用者その他の第三者に発生した機会損失、業務の中断その他いかなる損害（間接損害や逸失利益を含みます）に対して、当協会が係る損害の可能性を事前に通知されていたとしても、一切の責任を負いません。
              </li>
              <li>
                第１項ないし前項の規定は、当協会に故意または重過失が存する場合、もしくは利用者が消費者契約法において保護の対象となる消費者に該当する場合には適用しません。
              </li>
              <li>
                前項が適用される場合であっても、当協会は、過失（重過失を除きます。）による行為によって利用者に生じた損害のうち、特別な事情から生じた損害については、一切賠償する責任を負わないものとします。
              </li>
              <li>
                本アプリの利用に関し当協会が損害賠償責任を負う場合、当該損害が発生した月に利用者から受領した利用料金を限度として賠償責任を負うものとします。
              </li>
              <li>
                利用者と他の利用者との間の紛争およびトラブルについて、当協会は一切責任を負わないものとします。利用者と他の利用者でトラブルになった場合でも、当事者同士の責任で解決するものとし、当協会には一切の請求をしないものとします。
              </li>
              <li>
                利用者は、本アプリの利用に関連し、他の利用者に損害を与えた場合または第三者との間に紛争を生じた場合、自己の費用と責任において、かかる損害を賠償またはかかる紛争を解決するものとし、当協会には一切の迷惑や損害を与えないものとします。
              </li>
              <li>
                利用者の行為により、第三者から当協会が損害賠償等の請求をされた場合には、利用者の費用（弁護士費用）と責任で、これを解決するものとします。当協会が、当該第三者に対して、損害賠償金を支払った場合には、利用者は、当協会に対して当該損害賠償金を含む一切の費用（弁護士費用および逸失利益を含む）を支払うものとします。
              </li>
              <li>
                利用者が本アプリの利用に関連して当協会に損害を与えた場合、利用者の費用と責任において当協会に対して損害を賠償（訴訟費用および弁護士費用を含む）するものとします。
              </li>
            </ol>
          </section>

          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">
              第１２条（広告の掲載について）{' '}
            </h3>
            <p className="pl-6">
              利用者は、本アプリ上にあらゆる広告が含まれる場合があること、当協会またはその提携先があらゆる広告を掲載する場合があることを理解しこれを承諾したものとみなします。本アプリ上の広告の形態や範囲は、当協会によって随時変更されます。{' '}
            </p>
          </section>

          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">
              第１３条（権利譲渡の禁止）
            </h3>
            <ol className="list-decimal pl-6 space-y-2">
              <li>
                利用者は、予め当協会の書面による承諾がない限り、本利用規約上の地位および本利用規約に基づく権利または義務の全部または一部を第三者に譲渡してはならないものとします。
              </li>
              <li>
                当協会は、本アプリの全部または一部を当協会の裁量により第三者に譲渡することができ、その場合、譲渡された権利の範囲内で利用者のアカウントを含む、本アプリに係る利用者の一切の権利が譲渡先に移転するものとします。
              </li>
            </ol>
          </section>

          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">
              第１４条（反社会的勢力の排除）
            </h3>
            <ol className="list-decimal pl-6 space-y-2">
              <li>
                当協会および利用者は、それぞれ相手方に対し、次の各号に掲げる事項を確約するものとします。
                <ol className="list-decimal pl-6 mt-2 space-y-1">
                  <li>
                    自らが、暴力団、暴力団員、暴力団員でなくなった時から５年を経過していない者、暴力団準構成員、暴力団関係企業、総会屋等その他これらに準ずる者またはその構成員（以下、総称して「反社会的勢力」という）ではないこと
                  </li>
                  <li>
                    自らの役員（取締役、執行役、執行役員、業務を執行する社員、監査役またはこれらに準ずる者をいう）が反社会的勢力ではないこと
                  </li>
                  <li>
                    反社会的勢力に自己の名義を利用させ、本利用契約を締結するものでないこと
                  </li>
                  <li>
                    自らまたは第三者を利用して、本利用契約に関して相手方に対する脅迫的な言動もしくは暴力を用いる行為、または偽計もしくは威力を用いて相手方の業務を妨害し、もしくは信用を毀損する行為をしないこと
                  </li>
                </ol>
              </li>
              <li>
                当協会および利用者は、相手方が次の各号のいずれかに該当した場合には本利用契約を何らの催告を要しないで、直ちに解除することができるものとします。
                <ol className="list-decimal pl-6 mt-2 space-y-1">
                  <li>
                    前項第１号または第２号の確約に反する申告ないし表明をしたことが判明した場合
                  </li>
                  <li>
                    前項第３号の確約に反し、本利用契約を締結したことが判明した場合
                  </li>
                  <li>前項第４号の確約に反する行為をした場合</li>
                </ol>
              </li>
              <li>
                前項の規定により、本利用契約が解除された場合には、解除された者は、その相手方に対し、相手方の被った損害を賠償する。
              </li>
              <li>
                第２項の規定により、本利用契約が解除された場合には、解除された者は、解除により生じた損害について、その相手方に対し一切の請求を行わない。
              </li>
            </ol>
          </section>

          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">第１５条（契約の解除）</h3>
            <ol className="list-decimal pl-6 space-y-2">
              <li>
                <ol className="list-decimal pl-6 mt-2 space-y-1">
                  アートシフトコーチが次の各号のいずれかに該当する場合には、当協会は、当該アートシフトコーチとの契約を解除できるものとします。
                  <li>
                    当協会に提供した登録情報に偽りの内容が含まれていたとき
                  </li>
                  <li>
                    当協会のシステム、設備等を故意または重過失により破壊したとき
                  </li>
                  <li>
                    利用料金、その他の諸費用のクレジットカード決済が不能となったとき
                  </li>
                  <li>利用料金の支払いを怠ったとき</li>
                  <li>本利用規約に違反したとき</li>
                </ol>
              </li>
              <li>
                上記に該当したアートシフトコーチは、当協会に対して損害賠償請求や利用料金の返金請求などをすることはできません。
              </li>
              <li>
                なおアートシフトコーチは、当協会に規約違反などがあった場合の利用料金の返金請求はすることができます。
              </li>
            </ol>
          </section>

          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">第１６条（分離可能性）</h3>
            <p className="pl-6">
              本利用規約のいずれかの条項またはその一部が、消費者契約法その他の法令等により無効または執行不能と判断された場合であっても、本利用規約の残りの規定および一部が無効または執行不能と判断された規定の残りの部分は、継続して完全に効力を有するものとします。
            </p>
          </section>

          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">
              第１７条（当協会への連絡方法）
            </h3>
            <p className="pl-6">
              本アプリに関する利用者の当協会へのご連絡・お問い合わせは、本アプリまたは当協会が運営するＷｅｂサイト内の適宜の場所に設置するお問い合わせフォームからの送信または当協会が別途指定する方法により行うものとします。
            </p>
          </section>

          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">
              第１８条（準拠法、管轄裁判所）
            </h3>
            <ol className="list-decimal pl-6 space-y-2">
              <li>
                本利用規約の有効性，解釈および履行については，日本法に準拠し，日本法に従って解釈されるものとする。
              </li>
              <li>
                当協会と利用者等との間での論議・訴訟その他一切の紛争については、訴額に応じて、東京簡易裁判所または東京地方裁判所を専属的合意管轄裁判所とします｡
              </li>
            </ol>
          </section>

          <section className="mt-6">
            <h3 className="text-lg font-bold mb-2">
              <p>２０２４年７月１日 施行</p>
              <p>２０２４年１２月２６日 改定</p>
            </h3>
          </section>
        </div>

        <div
          className="border-t border-gray-200 pt-4 justify-center items-center mt-2 text-primary-500 flex hover:cursor-pointer"
          onClick={handleShowMore}
        >
          <div>{isShowMore ? '表示を減らす' : 'もっと見る'}</div>
          <span
            className={classNames({ 'rotate-180 transition-all': isShowMore })}
          >
            <IconShowMore />
          </span>
        </div>
      </div>
    </div>
  );
};

export default RegisterAccountTermService;
